import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  ArrowLeft, BookOpen, MessageSquare, Brain, Volume2,
  ChevronRight, GraduationCap, Grid3X3, ChevronLeft, CheckCircle, Play, Award, Lock, Compass, Image, Headphones, PenTool
} from 'lucide-react';
import { toast } from 'sonner';
import AudioPlayer from '../components/AudioPlayer';
import { categoryIcons, getCategoryIcon, getSortedCategories, CATEGORY_ORDER } from '../utils/categoryConfig';
import { Progress } from '../components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Catégories en mode "Immersion Image" - masque les traductions
const IMMERSION_CATEGORIES = [
  'couleurs', 'colors',
  'maison', 'house', 'home',
  'fruits', 'légumes', 'fruits et légumes', 'vegetables', 'fruits and vegetables',
  'nourriture', 'food',
  'lieux', 'places', 'locations',
  'plats', 'dishes', 'meals',
  'moyens de transport', 'transport', 'transportation',
  'sport', 'sports',
  'école', 'school', 'education',
  'vêtements', 'clothes', 'clothing'
];

// Fonction pour vérifier si une catégorie est en mode immersion
const isImmersionCategory = (category) => {
  if (!category) return false;
  const normalizedCategory = category.toLowerCase().trim();
  return IMMERSION_CATEGORIES.some(immCat => 
    normalizedCategory.includes(immCat) || immCat.includes(normalizedCategory)
  );
};

const LevelPage = () => {
  const { levelId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { scriptMode } = usePreferences();
  const { language } = useLanguage();
  
  const [levelData, setLevelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vocabulary');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [grandQuizStatus, setGrandQuizStatus] = useState(null);
  const [categoryOrder, setCategoryOrder] = useState(null); // Dynamic order from backend
  const [premiumConfig, setPremiumConfig] = useState(null); // Freemium config
  const [categoriesWithImages, setCategoriesWithImages] = useState([]); // Categories with enough images for image quiz
  const [categoriesWithAudio, setCategoriesWithAudio] = useState([]); // Categories with enough audio for listening quiz
  const [quizProgress, setQuizProgress] = useState([]); // Quiz progress for vocabulary bars
  const [contentProgress, setContentProgress] = useState([]); // Content progress for situations/grammar
  const [listenedExamples, setListenedExamples] = useState(new Set()); // Track listened grammar examples
  const [listenedPhrases, setListenedPhrases] = useState(new Set()); // Track listened situation phrases

  useEffect(() => {
    fetchLevelContent();
    fetchGrandQuizStatus();
    fetchCategoryOrder();
    fetchPremiumConfig();
    fetchCategoriesWithImages();
    fetchCategoriesWithAudio();
    fetchAllProgress();
  }, [levelId]);

  const fetchAllProgress = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [quizRes, contentRes] = await Promise.all([
        axios.get(`${API}/user/quiz-progress`, { headers }),
        axios.get(`${API}/user/content-progress`, { headers })
      ]);
      setQuizProgress(quizRes.data.progress || []);
      setContentProgress(contentRes.data.progress || []);
    } catch (error) {
      console.log('Could not fetch progress (guest mode)');
    }
  };

  // Build vocabulary progress map: (category_lowercase, level) -> best percentage
  // Includes both vocabulary and listening quiz types
  const vocabProgressMap = useMemo(() => {
    const map = {};
    const levelMap = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
    const currentLevel = levelMap[levelData?.level?.level_number] || 'beginner';
    
    quizProgress
      .filter(p => ['vocabulary', 'listening'].includes(p.quiz_type) && p.level === currentLevel)
      .forEach(p => {
        const key = (p.category || '').toLowerCase().trim();
        if (!map[key] || (p.percentage || 0) > (map[key] || 0)) {
          map[key] = p.percentage || 0;
        }
      });
    return map;
  }, [quizProgress, levelData]);

  // Build image quiz progress map: (category_lowercase) -> best percentage
  const imageQuizProgressMap = useMemo(() => {
    const map = {};
    const levelMap = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
    const currentLevel = levelMap[levelData?.level?.level_number] || 'beginner';
    
    quizProgress
      .filter(p => p.quiz_type === 'image' && p.level === currentLevel)
      .forEach(p => {
        const key = (p.category || '').toLowerCase().trim();
        if (!map[key] || (p.percentage || 0) > (map[key] || 0)) {
          map[key] = p.percentage || 0;
        }
      });
    return map;
  }, [quizProgress, levelData]);

  // Build grammar progress map: grammar_id -> { examplesListened, totalExamples, quizPassed }
  const grammarProgressMap = useMemo(() => {
    const map = {};
    const levelMap = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
    const currentLevel = levelMap[levelData?.level?.level_number] || 'beginner';
    
    // Read grammar progress items
    contentProgress
      .filter(p => p.content_type === 'grammar_read' && p.level === currentLevel)
      .forEach(p => {
        if (!map[p.content_id]) map[p.content_id] = { examplesListened: 0, totalExamples: 0, quizPassed: false };
        map[p.content_id].examplesListened = p.examples_listened || 0;
        map[p.content_id].totalExamples = p.total_examples || 0;
      });
    
    // Mark quiz passed (quiz_type='grammar' where category=lesson_id)
    quizProgress
      .filter(p => p.quiz_type === 'grammar' && p.level === currentLevel && p.completed)
      .forEach(p => {
        const lessonId = p.category; // category contains grammar lesson id
        if (!map[lessonId]) map[lessonId] = { examplesListened: 0, totalExamples: 0, quizPassed: false };
        map[lessonId].quizPassed = true;
      });
    
    return map;
  }, [quizProgress, contentProgress, levelData]);

  // Build situations progress: calculate from completed phrases / total phrases
  const situationsProgressMap = useMemo(() => {
    const levelMap = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
    const currentLevel = levelMap[levelData?.level?.level_number] || 'beginner';
    
    // Get all situation progress for this level
    const situationProgress = contentProgress.filter(
      p => p.content_type === 'situation' && p.level === currentLevel
    );
    
    // Calculate total phrases from ALL situations in the level
    let totalPhrasesOverall = 0;
    (levelData?.situations || []).forEach(s => {
      totalPhrasesOverall += s.phrases?.length || 0;
    });
    
    // Calculate completed phrases from progress records
    let totalPhrasesCompleted = 0;
    (levelData?.situations || []).forEach(s => {
      const rec = situationProgress.find(p => p.content_id === s.id);
      const sitTotal = s.phrases?.length || 0;
      const done = rec?.completed ? sitTotal : Math.min(rec?.phrases_completed || 0, sitTotal);
      totalPhrasesCompleted += done;
    });
    
    return totalPhrasesOverall > 0
      ? Math.min(100, Math.round((totalPhrasesCompleted / totalPhrasesOverall) * 100))
      : 0;
  }, [contentProgress, levelData]);

  // Get individual situation progress
  const getSituationProgress = (situationId) => {
    const progress = contentProgress.find(
      p => p.content_type === 'situation' && p.content_id === situationId
    );
    return {
      phrasesCompleted: progress?.phrases_completed || 0,
      totalPhrases: progress?.total_phrases || 0,
      completed: progress?.completed || false
    };
  };

  const fetchPremiumConfig = async () => {
    try {
      const response = await axios.get(`${API}/premium/config`);
      setPremiumConfig(response.data);
    } catch (error) {
      console.log('Could not fetch premium config');
    }
  };

  const getLevelStr = async () => {
    const levelMap = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
    try {
      const { data } = await axios.get(`${API}/levels`);
      const match = data.find((l) => l.id === levelId);
      return levelMap[match?.level_number] || 'beginner';
    } catch (error) {
      return 'beginner';
    }
  };

  const fetchCategoriesWithImages = async () => {
    const levelStr = await getLevelStr();
    try {
      const response = await axios.get(`${API}/categories/with-images?level=${levelStr}`);
      setCategoriesWithImages(response.data.categories || []);
    } catch (error) {
      console.log('Could not fetch categories with images');
    }
  };

  const fetchCategoriesWithAudio = async () => {
    const levelStr = await getLevelStr();
    try {
      const response = await axios.get(`${API}/categories/with-audio?level=${levelStr}`);
      setCategoriesWithAudio(response.data.categories || []);
    } catch (error) {
      console.log('Could not fetch categories with audio');
    }
  };

  const fetchCategoryOrder = async () => {
    const levelStr = await getLevelStr();
    try {
      const response = await axios.get(`${API}/category-order/${levelStr}`);
      if (response.data?.categories?.length > 0) {
        setCategoryOrder(response.data.categories);
      }
    } catch (error) {
      console.log('Using default category order');
    }
  };

  // Mark content as read/viewed (generic)
  const markContentProgress = async (contentType, contentId, level, extraData = {}) => {
    if (!token) return; // Guest mode - don't track
    try {
      await axios.post(`${API}/user/content-progress`, {
        content_type: contentType,
        content_id: contentId,
        level: level,
        ...extraData
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Refresh progress
      fetchAllProgress();
    } catch (error) {
      console.log('Could not save content progress');
    }
  };

  // Mark a grammar example as listened
  const markGrammarExampleListened = (grammarId, exampleIndex, totalExamples) => {
    const key = `${grammarId}:${exampleIndex}`;
    if (listenedExamples.has(key)) return; // Already listened
    
    const newSet = new Set(listenedExamples);
    newSet.add(key);
    setListenedExamples(newSet);
    
    // Count listened examples for this grammar lesson
    const listenedForLesson = Array.from(newSet).filter(k => k.startsWith(`${grammarId}:`)).length;
    
    // Send progress to backend
    const levelStr = levelData?.level?.level_number === 1 ? 'beginner' 
      : levelData?.level?.level_number === 2 ? 'intermediate' : 'advanced';
    
    markContentProgress('grammar_read', grammarId, levelStr, {
      examples_listened: listenedForLesson,
      total_examples: totalExamples
    });
  };

  // Mark a situation phrase as listened
  const markSituationPhraseListened = (situationId, phraseIndex, totalPhrases) => {
    const key = `${situationId}:${phraseIndex}`;
    if (listenedPhrases.has(key)) return; // Already listened
    
    const newSet = new Set(listenedPhrases);
    newSet.add(key);
    setListenedPhrases(newSet);
    
    // Count listened phrases for this situation
    const listenedForSituation = Array.from(newSet).filter(k => k.startsWith(`${situationId}:`)).length;
    
    // Send progress to backend
    const levelStr = levelData?.level?.level_number === 1 ? 'beginner' 
      : levelData?.level?.level_number === 2 ? 'intermediate' : 'advanced';
    
    markContentProgress('situation', situationId, levelStr, {
      phrases_completed: listenedForSituation,
      total_phrases: totalPhrases
    });
  };

  // Get count of listened examples for a grammar lesson (for current session + stored)
  const getListenedExamplesCount = (grammarId, totalExamples) => {
    // Count from current session
    const sessionCount = Array.from(listenedExamples).filter(k => k.startsWith(`${grammarId}:`)).length;
    // Get stored count from backend
    const storedCount = grammarProgressMap[grammarId]?.examplesListened || 0;
    return Math.max(sessionCount, storedCount);
  };

  // Get count of listened phrases for a situation (for current session + stored)
  const getListenedPhrasesCount = (situationId) => {
    // Count from current session
    const sessionCount = Array.from(listenedPhrases).filter(k => k.startsWith(`${situationId}:`)).length;
    // Get stored count from backend
    const storedProgress = getSituationProgress(situationId);
    return Math.max(sessionCount, storedProgress.phrasesCompleted);
  };

  const fetchLevelContent = async () => {
    try {
      // Try to load cached data first
      const cacheKey = `morocotalk_level_${levelId}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        setLevelData(JSON.parse(cachedData));
        setLoading(false); // Show cached data immediately
      }
      
      // Fetch fresh data from server
      const response = await axios.get(`${API}/levels/${levelId}/content`);
      setLevelData(response.data);
      
      // Cache the data
      localStorage.setItem(cacheKey, JSON.stringify(response.data));
      localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
      
    } catch (error) {
      console.error('Failed to fetch level content:', error);
      
      // If server unavailable, use cached data
      const cacheKey = `morocotalk_level_${levelId}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        setLevelData(JSON.parse(cachedData));
        toast.info(language === 'en' 
          ? 'Using cached data. Some features may be limited.' 
          : 'Utilisation des données en cache.');
      } else {
        toast.error(language === 'en' ? 'Error loading level' : 'Erreur de chargement du niveau');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchGrandQuizStatus = async () => {
    const levelStr = await getLevelStr();

    try {
      const response = await axios.get(`${API}/user/can-take-grand-quiz/${levelStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrandQuizStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch grand quiz status:', error);
    }
  };

  // Group vocabulary by category
  const vocabularyByCategory = useMemo(() => {
    if (!levelData?.vocabulary) return {};
    return levelData.vocabulary.reduce((acc, vocab) => {
      const cat = vocab.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(vocab);
      return acc;
    }, {});
  }, [levelData?.vocabulary]);

  // Check if user has premium access
  const isPremiumUser = user?.is_premium || user?.is_admin;
  
  // Get current level string
  const currentLevelStr = useMemo(() => {
    const levelMap = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
    return levelMap[levelData?.level?.level_number || 1] || 'beginner';
  }, [levelData?.level?.level_number]);

  // Check if a category is locked (Premium only)
  const isCategoryLocked = (category, index, totalCategories) => {
    if (isPremiumUser) return false;
    
    // Niveau 2 et 3 = 100% Premium
    if (currentLevelStr === 'intermediate' || currentLevelStr === 'advanced') {
      return true;
    }
    
    // Niveau 1 = Première moitié gratuite
    if (currentLevelStr === 'beginner') {
      const halfIndex = Math.floor(totalCategories / 2);
      return index >= halfIndex;
    }
    
    return false;
  };

  // Check if a grammar lesson is locked
  const isGrammarLocked = (grammarItem, index, totalGrammar) => {
    if (isPremiumUser) return false;
    
    if (currentLevelStr === 'intermediate' || currentLevelStr === 'advanced') {
      return true;
    }
    
    if (currentLevelStr === 'beginner') {
      const halfIndex = Math.floor(totalGrammar / 2);
      return index >= halfIndex;
    }
    
    return false;
  };

  // Check if a situation is locked
  const isSituationLocked = (situation, index, totalSituations) => {
    if (isPremiumUser) return false;
    
    if (currentLevelStr === 'intermediate' || currentLevelStr === 'advanced') {
      return true;
    }
    
    if (currentLevelStr === 'beginner') {
      const halfIndex = Math.floor(totalSituations / 2);
      return index >= halfIndex;
    }
    
    return false;
  };

  const categories = useMemo(() => {
    const cats = Object.keys(vocabularyByCategory);
    const levelNumber = levelData?.level?.level_number || 1;
    
    // If we have a custom order from the backend, use it
    if (categoryOrder && categoryOrder.length > 0) {
      const orderMap = {};
      categoryOrder.forEach((c, index) => {
        orderMap[c.category?.toLowerCase()] = index;
      });
      
      return [...cats].sort((a, b) => {
        const aOrder = orderMap[a.toLowerCase()] ?? 9999;
        const bOrder = orderMap[b.toLowerCase()] ?? 9999;
        return aOrder - bOrder;
      });
    }
    
    // Fallback to static order
    return getSortedCategories(cats, levelNumber);
  }, [vocabularyByCategory, levelData, categoryOrder]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">
          {language === 'en' ? 'Loading...' : 'Chargement...'}
        </p>
      </div>
    );
  }

  if (!levelData) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <p className="font-nunito text-xl text-charcoal mb-4">
            {language === 'en' ? 'Level not found' : 'Niveau non trouvé'}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl"
          >
            {language === 'en' ? 'Back to Dashboard' : 'Retour au tableau de bord'}
          </button>
        </div>
      </div>
    );
  }

  const { level, vocabulary, grammar, situations, quizzes, culture = [] } = levelData;
  const levelTitle = language === 'en' ? level.title_en : level.title_fr;

  // Tabs without the standalone Quiz tab (kept only category quizzes and Grand Quiz)
  const tabs = [
    { id: 'vocabulary', label: language === 'en' ? 'Vocabulary' : 'Vocabulaire', icon: <BookOpen className="w-5 h-5" />, count: vocabulary.length },
    { id: 'grammar', label: language === 'en' ? 'Grammar' : 'Grammaire', icon: <GraduationCap className="w-5 h-5" />, count: grammar.length },
    { id: 'situations', label: 'Situations', icon: <MessageSquare className="w-5 h-5" />, count: situations.length },
    { id: 'culture', label: language === 'en' ? 'A-lMaghrib' : 'A-lMaghrib', icon: <Compass className="w-5 h-5" />, count: culture.length },
  ];

  const getLevelColor = () => {
    const colors = {
      1: 'from-emerald-500 to-emerald-600',
      2: 'from-blue-500 to-blue-600',
      3: 'from-purple-500 to-purple-600'
    };
    return colors[level.level_number] || 'from-gray-500 to-gray-600';
  };

  // Markdown components with styled tables
  const markdownComponents = {
    table: ({ children }) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full border-collapse border border-stone-300 rounded-lg overflow-hidden">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gradient-to-r from-primary to-orange-500 text-white">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody className="bg-white divide-y divide-stone-200">
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="hover:bg-sand/50 transition-colors">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="px-4 py-3 text-left font-nunito font-bold text-sm uppercase tracking-wider border-r border-orange-400 last:border-r-0">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-3 font-nunito text-charcoal border-r border-stone-200 last:border-r-0">
        {children}
      </td>
    ),
    strong: ({ children }) => (
      <strong className="font-bold text-primary">{children}</strong>
    ),
    p: ({ children }) => (
      <p className="mb-3 leading-relaxed">{children}</p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>
    ),
    li: ({ children }) => (
      <li className="text-charcoal/80">{children}</li>
    ),
  };

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <div className={`bg-gradient-to-r ${getLevelColor()} text-white py-8`}>
        <div className="container mx-auto px-4 sm:px-6">
          <button
            onClick={() => selectedCategory ? setSelectedCategory(null) : navigate('/dashboard')}
            data-testid="back-btn"
            className="mb-4 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-nunito">
              {selectedCategory 
                ? (language === 'en' ? 'Back to categories' : 'Retour aux catégories')
                : (language === 'en' ? 'Back' : 'Retour')}
            </span>
          </button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-playfair text-3xl sm:text-4xl font-bold mb-2">
              {levelTitle}
              {selectedCategory && (
                <span className="text-white/80 ml-2">/ {selectedCategory}</span>
              )}
            </h1>
            <p className="font-nunito text-lg opacity-90">
              {language === 'en' ? level.description_en : level.description_fr}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Content Summary */}
        {vocabulary.length === 0 && grammar.length === 0 && situations.length === 0 && quizzes.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center border-2 border-stone-200"
          >
            <div className="text-6xl mb-4">📚</div>
            <h2 className="font-playfair text-2xl font-bold text-charcoal mb-4">
              {language === 'en' ? 'No content yet' : 'Pas encore de contenu'}
            </h2>
            <p className="font-nunito text-charcoal/70 mb-6">
              {language === 'en' 
                ? 'The administrator has not added content to this level yet. Please check back later!'
                : "L'administrateur n'a pas encore ajouté de contenu à ce niveau. Revenez plus tard !"}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b]"
            >
              {language === 'en' ? 'Back to Dashboard' : 'Retour au tableau de bord'}
            </button>
          </motion.div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSelectedCategory(null);
                  }}
                  data-testid={`tab-${tab.id}`}
                  className={`tile-button font-nunito font-bold py-3 px-5 rounded-xl flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-primary text-white shadow-[0_4px_0_#9a360b]'
                      : 'bg-white text-charcoal border-2 border-stone-200 hover:border-primary'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-white/20' : 'bg-stone-200'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
              
              {/* Grand Quiz Button */}
              <button
                onClick={() => {
                  const levelMap = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
                  const levelStr = levelMap[level.level_number] || 'beginner';
                  navigate(`/grand-quiz?level=${levelStr}`);
                }}
                data-testid="grand-quiz-btn"
                className={`tile-button font-nunito font-bold py-3 px-5 rounded-xl flex items-center gap-2 ml-auto ${
                  grandQuizStatus?.can_take_grand_quiz
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-[0_4px_0_#b45309]'
                    : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                }`}
              >
                {grandQuizStatus?.can_take_grand_quiz ? (
                  <Award className="w-5 h-5" />
                ) : (
                  <Lock className="w-5 h-5" />
                )}
                Grand Quiz
                {grandQuizStatus && !grandQuizStatus.can_take_grand_quiz && (
                  <span className="text-xs">
                    ({grandQuizStatus.completed_categories?.length || 0}/3)
                  </span>
                )}
              </button>
            </div>

            {/* Vocabulary Tab - Category Grid or Word List */}
            {activeTab === 'vocabulary' && (
              <AnimatePresence mode="wait">
                {!selectedCategory ? (
                  // Category Grid
                  <motion.div
                    key="categories"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <div className="flex items-center gap-2 mb-6">
                      <Grid3X3 className="w-6 h-6 text-primary" />
                      <h3 className="font-playfair text-2xl font-bold text-charcoal">
                        {language === 'en' ? 'Categories' : 'Catégories'}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {categories.map((category, index) => {
                        const wordCount = vocabularyByCategory[category].length;
                        const hasEnoughForQuiz = wordCount >= 4;
                        const isLocked = isCategoryLocked(category, index, categories.length);
                        const vocabPercent = vocabProgressMap[category.toLowerCase().trim()] || 0;
                        const imageQuizPercent = imageQuizProgressMap[category.toLowerCase().trim()] || 0;
                        
                        return (
                          <motion.div
                            key={category}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className={`bg-white rounded-2xl shadow-lg border-2 ${isLocked ? 'border-amber-300 bg-amber-50/50' : 'border-stone-200'} hover:shadow-xl transition-all overflow-hidden relative`}
                          >
                            {isLocked && (
                              <div className="absolute top-2 right-2 bg-amber-500 text-white p-1.5 rounded-full z-10">
                                <Lock className="w-4 h-4" />
                              </div>
                            )}
                            <button
                              onClick={() => {
                                if (isLocked) {
                                  toast.error(language === 'en' ? 'Premium content - Upgrade to access!' : 'Contenu Premium - Passez à Premium !');
                                  navigate('/premium');
                                } else {
                                  setSelectedCategory(category);
                                }
                              }}
                              data-testid={`category-${category}`}
                              className={`w-full p-6 text-left transition-colors ${isLocked ? 'opacity-75' : 'hover:bg-stone-50'}`}
                            >
                              <div className="text-5xl mb-3">
                                {getCategoryIcon(category, levelData?.category_icons?.[category])}
                              </div>
                              <h4 className="font-nunito font-bold text-lg text-charcoal capitalize mb-1">
                                {category}
                              </h4>
                              <p className="font-nunito text-sm text-charcoal/60 mb-2">
                                {wordCount} {language === 'en' ? 'words' : 'mots'}
                                {isLocked && <span className="ml-2 text-amber-600">🔒</span>}
                              </p>
                              {!isLocked && vocabPercent > 0 && (
                                <div className="mt-2">
                                  <Progress value={Math.round(vocabPercent)} className="h-1.5" />
                                  <p className="text-xs text-primary mt-1">{Math.round(vocabPercent)}%</p>
                                </div>
                              )}
                            </button>
                            
                            {/* Quiz buttons - stacked list based on eligibility */}
                            {hasEnoughForQuiz && !isLocked && (
                              <div className="border-t border-stone-200 p-2 space-y-2">
                                {/* Standard Quiz - always shown if ≥4 words */}
                                <button
                                  onClick={() => navigate(`/category-quiz?category=${encodeURIComponent(category)}&level=${level.level_number === 1 ? 'beginner' : level.level_number === 2 ? 'intermediate' : 'advanced'}`)}
                                  data-testid={`quiz-${category}`}
                                  className="w-full py-2 px-3 bg-primary/10 hover:bg-primary/20 text-primary font-nunito font-semibold text-sm flex items-center justify-center gap-2 transition-colors rounded-lg"
                                >
                                  <Brain className="w-4 h-4" />
                                  {language === 'en' ? 'Quiz' : 'Quiz'}
                                </button>
                                
                                {/* Image Quiz - if category has enough images */}
                                {categoriesWithImages.includes(category) && (
                                  <button
                                    onClick={() => navigate(`/category-quiz?category=${encodeURIComponent(category)}&level=${level.level_number === 1 ? 'beginner' : level.level_number === 2 ? 'intermediate' : 'advanced'}&mode=image`)}
                                    data-testid={`quiz-image-${category}`}
                                    className="w-full py-2 px-3 bg-accent/10 hover:bg-accent/20 text-amber-700 font-nunito font-semibold text-sm flex items-center justify-center gap-2 transition-colors rounded-lg"
                                  >
                                    <Image className="w-4 h-4" />
                                    {language === 'en' ? 'Image Quiz' : 'Quiz Image'}
                                  </button>
                                )}
                                
                                {/* Listening Quiz - if category has enough audio */}
                                {categoriesWithAudio.includes(category) && (
                                  <button
                                    onClick={() => navigate(`/category-quiz?category=${encodeURIComponent(category)}&level=${level.level_number === 1 ? 'beginner' : level.level_number === 2 ? 'intermediate' : 'advanced'}&mode=listening`)}
                                    data-testid={`quiz-listening-${category}`}
                                    className="w-full py-2 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 font-nunito font-semibold text-sm flex items-center justify-center gap-2 transition-colors rounded-lg"
                                  >
                                    <Headphones className="w-4 h-4" />
                                    {language === 'en' ? 'Listening Quiz' : 'Quiz Écoute'}
                                  </button>
                                )}
                                
                                {/* Progress bar for vocab quiz */}
                                {vocabPercent > 0 && (
                                  <div className="mt-1 px-1">
                                    <Progress value={Math.round(vocabPercent)} className="h-1.5" />
                                    <p className="text-xs text-primary mt-1 text-center">{Math.round(vocabPercent)}%</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                ) : (
                  // Word List for Selected Category
                  <motion.div
                    key="words"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setSelectedCategory(null)}
                          className="p-2 bg-stone-200 rounded-lg hover:bg-stone-300 transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5 text-charcoal" />
                        </button>
                        <span className="text-4xl">{getCategoryIcon(selectedCategory, levelData?.category_icons?.[selectedCategory])}</span>
                        <div>
                          <h3 className="font-playfair text-2xl font-bold text-charcoal capitalize">
                            {selectedCategory}
                          </h3>
                          <p className="font-nunito text-sm text-charcoal/60">
                            {vocabularyByCategory[selectedCategory]?.length || 0} {language === 'en' ? 'words' : 'mots'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Quiz button for this category */}
                      {(vocabularyByCategory[selectedCategory]?.length || 0) >= 4 && (
                        <button
                          onClick={() => navigate(`/category-quiz?category=${encodeURIComponent(selectedCategory)}&level=${level.level_number === 1 ? 'beginner' : level.level_number === 2 ? 'intermediate' : 'advanced'}`)}
                          className="tile-button bg-primary text-white font-nunito font-bold py-2 px-4 rounded-xl shadow-[0_3px_0_#9a360b] flex items-center gap-2"
                        >
                          <Brain className="w-5 h-5" />
                          {language === 'en' ? 'Quiz' : 'Quiz'}
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {(vocabularyByCategory[selectedCategory] || []).map((vocab, index) => {
                        const immersionMode = levelData?.categories_hide_translation?.[selectedCategory] ?? isImmersionCategory(selectedCategory);
                        const imageUrl = vocab.image_url 
                          ? (vocab.image_url.startsWith('http') ? vocab.image_url : `${BACKEND_URL}${vocab.image_url}`)
                          : null;
                        
                        return (
                          <motion.div
                            key={vocab.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="bg-white rounded-xl shadow-sm p-5 border-2 border-stone-200 hover:shadow-lg hover:border-primary/30 transition-all"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <p className="font-nunito font-bold text-xl text-primary">
                                  {scriptMode === 'arabic' ? (vocab.word_darija_arabic || vocab.word_darija_phonetic) : vocab.word_darija_phonetic}
                                </p>
                              </div>
                              <AudioPlayer audioUrl={vocab.audio_url} size="md" />
                            </div>
                            
                            {/* Image if available */}
                            {imageUrl && (
                              <img 
                                src={imageUrl}
                                alt={vocab.word_darija_phonetic}
                                referrerPolicy="no-referrer"
                                className="w-full h-24 object-contain bg-stone-100 rounded-lg mb-3"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            
                            {/* Traduction masquée en mode immersion */}
                            {!immersionMode && (
                              <p className="font-nunito text-charcoal text-lg">
                                {language === 'en' ? vocab.word_english : vocab.word_french}
                              </p>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            {/* Grammar Tab with Markdown Rendering */}
            {activeTab === 'grammar' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                {grammar.length === 0 ? (
                  <div className="text-center py-12 text-charcoal/50 font-nunito">
                    {language === 'en' ? 'No grammar for this level' : 'Pas de grammaire pour ce niveau'}
                  </div>
                ) : (
                  grammar.map((g, index) => {
                    const isLocked = isGrammarLocked(g, index, grammar.length);
                    const grammarProg = grammarProgressMap[g.id] || { examplesListened: 0, totalExamples: 0, quizPassed: false };
                    const totalExamples = g.examples?.length || 0;
                    const hasQuiz = totalExamples >= 2;
                    const listenedCount = getListenedExamplesCount(g.id, totalExamples);
                    
                    // Calculate progress: 50% listening (examples) + 50% quiz (or 100% listening if no quiz)
                    let grammarPercent = 0;
                    if (totalExamples > 0) {
                      const listeningPercent = (listenedCount / totalExamples) * 100;
                      if (hasQuiz) {
                        grammarPercent = Math.round((listeningPercent / 2) + (grammarProg.quizPassed ? 50 : 0));
                      } else {
                        grammarPercent = Math.round(listeningPercent);
                      }
                    } else if (grammarProg.quizPassed) {
                      grammarPercent = 100;
                    }
                    
                    return (
                      <motion.div
                        key={g.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`bg-white rounded-2xl shadow-lg p-6 sm:p-8 border-2 ${isLocked ? 'border-amber-300 bg-amber-50/30' : 'border-stone-200'} relative`}
                      >
                        {isLocked && (
                          <div className="absolute top-4 right-4 bg-amber-500 text-white p-2 rounded-full z-10">
                            <Lock className="w-5 h-5" />
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-4xl">{g.icon}</span>
                          <div className="flex-1">
                            <h3 className="font-playfair text-2xl font-bold text-charcoal">{g.title}</h3>
                            {isLocked && <span className="text-amber-600 font-nunito text-sm">Premium</span>}
                          </div>
                        </div>
                        
                        {/* Progress bar for grammar */}
                        {!isLocked && grammarPercent > 0 && (
                          <div className="mb-4">
                            <Progress value={grammarPercent} className="h-1.5" />
                            <p className="text-xs text-primary mt-1">
                              {grammarPercent}% 
                              {totalExamples > 0 && ` (${listenedCount}/${totalExamples} exemples)`}
                              {hasQuiz ? (grammarProg.quizPassed ? ' ✓ Quiz réussi' : ' - Quiz à faire') : ''}
                            </p>
                          </div>
                        )}
                        
                        {isLocked ? (
                          <div className="text-center py-8">
                            <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                            <p className="font-nunito text-charcoal/70 mb-4">
                              {language === 'en' ? 'This lesson is Premium only' : 'Cette leçon est réservée aux Premium'}
                            </p>
                            <button
                              onClick={() => navigate('/premium')}
                              className="tile-button bg-gradient-to-r from-amber-500 to-orange-500 text-white font-nunito font-bold py-3 px-6 rounded-xl"
                            >
                              {language === 'en' ? 'Upgrade to Premium' : 'Passer à Premium'}
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Markdown Content with styled tables */}
                            <div className="prose prose-lg max-w-none font-nunito text-charcoal/80">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents}
                              >
                                {g.explanation}
                              </ReactMarkdown>
                            </div>

                            {g.examples && g.examples.length > 0 && (
                              <div className="mt-6 pt-6 border-t border-stone-200">
                                <h4 className="font-nunito font-bold text-lg text-charcoal mb-4 flex items-center gap-2">
                                  <span>📝</span>
                                  {language === 'en' ? 'Examples' : 'Exemples'}
                                  <span className="text-sm font-normal text-charcoal/50">
                                    ({listenedCount}/{totalExamples})
                                  </span>
                                </h4>
                                <div className="grid gap-3">
                                  {g.examples.map((ex, i) => (
                                    <div key={i} className="bg-gradient-to-r from-sand to-sand/50 rounded-xl p-4 border border-stone-200">
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1">
                                          <p className="font-nunito font-bold text-lg text-primary mb-1">
                                            {scriptMode === 'arabic' ? ex.darija_arabic : ex.darija_phonetic}
                                          </p>
                                          <p className="font-nunito text-charcoal/70">
                                            {language === 'en' ? ex.english : ex.french}
                                          </p>
                                        </div>
                                        {/* Audio player for grammar examples with listening tracking */}
                                        <AudioPlayer 
                                          audioUrl={ex.audio_url} 
                                          size="sm" 
                                          onListened={() => markGrammarExampleListened(g.id, i, totalExamples)}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Mini-Quiz button for this grammar lesson */}
                                {g.examples.length >= 2 && (
                                  <div className="mt-6 pt-4 border-t border-stone-200">
                                    <button
                                      onClick={() => navigate(`/category-quiz?grammar=${g.id}&level=${level.level_number === 1 ? 'beginner' : level.level_number === 2 ? 'intermediate' : 'advanced'}`)}
                                      data-testid={`grammar-quiz-${g.id}`}
                                      className="tile-button bg-secondary/10 hover:bg-secondary text-secondary hover:text-white font-nunito font-bold py-3 px-6 rounded-xl transition-colors flex items-center gap-2 w-full justify-center"
                                    >
                                      <Brain className="w-5 h-5" />
                                      {language === 'en' ? 'Practice Quiz' : 'Mini-Quiz Pratique'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </motion.div>
                    );
                  })
                )}
              </motion.div>
            )}

            {/* Situations Tab */}
            {activeTab === 'situations' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                {/* Global situations progress */}
                {situations.length > 0 && situationsProgressMap > 0 && (
                  <div className="bg-white rounded-xl p-4 border-2 border-stone-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-nunito text-sm text-charcoal/70">
                        {language === 'en' ? 'Situations listened' : 'Situations écoutées'}
                      </span>
                      <span className="font-nunito text-sm text-primary font-bold">{Math.round(situationsProgressMap)}%</span>
                    </div>
                    <Progress value={situationsProgressMap} className="h-2" />
                  </div>
                )}
                
                {situations.length === 0 ? (
                  <div className="text-center py-12 text-charcoal/50 font-nunito">
                    {language === 'en' ? 'No situations for this level' : 'Pas de situations pour ce niveau'}
                  </div>
                ) : (
                  situations.map((s, index) => {
                    const isLocked = isSituationLocked(s, index, situations.length);
                    const levelStr = level.level_number === 1 ? 'beginner' : level.level_number === 2 ? 'intermediate' : 'advanced';
                    const sitProgress = getSituationProgress(s.id);
                    const totalPhrases = s.phrases?.length || 0;
                    const listenedPhrasesForSit = getListenedPhrasesCount(s.id);
                    const isCompleted = sitProgress.completed || (totalPhrases > 0 && listenedPhrasesForSit >= totalPhrases);
                    const situationPercent = totalPhrases > 0 ? Math.round((listenedPhrasesForSit / totalPhrases) * 100) : 0;
                    
                    return (
                      <motion.div
                        key={s.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`bg-white rounded-2xl shadow-lg p-6 border-2 ${isLocked ? 'border-amber-300 bg-amber-50/30' : isCompleted ? 'border-green-300' : 'border-stone-200'} relative`}
                      >
                        {isLocked && (
                          <div className="absolute top-4 right-4 bg-amber-500 text-white p-2 rounded-full z-10">
                            <Lock className="w-5 h-5" />
                          </div>
                        )}
                        {!isLocked && isCompleted && (
                          <div className="absolute top-4 right-16 bg-green-500 text-white p-1.5 rounded-full z-10">
                            <CheckCircle className="w-4 h-4" />
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <span className="text-4xl">{s.icon}</span>
                            <div>
                              <h3 className="font-playfair text-2xl font-bold text-charcoal">{s.title}</h3>
                              {!isLocked && totalPhrases > 0 && (
                                <p className="text-xs text-charcoal/50">
                                  {listenedPhrasesForSit}/{totalPhrases} {language === 'en' ? 'phrases' : 'phrases'}
                                </p>
                              )}
                            </div>
                            {isLocked && <span className="text-amber-600 font-nunito text-sm">Premium</span>}
                          </div>
                          {!isLocked && <AudioPlayer audioUrl={s.audio_url} size="lg" />}
                        </div>
                        
                        {/* Progress bar for situation */}
                        {!isLocked && situationPercent > 0 && (
                          <div className="mb-4">
                            <Progress value={situationPercent} className="h-1.5" />
                            <p className="text-xs text-primary mt-1">{situationPercent}%</p>
                          </div>
                        )}
                        
                        {isLocked ? (
                          <div className="text-center py-8">
                            <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                            <p className="font-nunito text-charcoal/70 mb-4">
                              {language === 'en' ? 'This situation is Premium only' : 'Cette situation est réservée aux Premium'}
                            </p>
                            <button
                              onClick={() => navigate('/premium')}
                              className="tile-button bg-gradient-to-r from-amber-500 to-orange-500 text-white font-nunito font-bold py-3 px-6 rounded-xl"
                            >
                              {language === 'en' ? 'Upgrade to Premium' : 'Passer à Premium'}
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {s.phrases?.map((phrase, i) => (
                              <div key={i} className="bg-gradient-to-r from-sand to-sand/50 rounded-xl p-4 border border-stone-200">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1">
                                    <p className="font-nunito font-bold text-lg text-primary mb-1">
                                      {scriptMode === 'arabic' ? phrase.darija_arabic : phrase.darija_phonetic}
                                    </p>
                                    <p className="font-nunito text-charcoal/70">
                                      {language === 'en' ? phrase.english : phrase.french}
                                    </p>
                                  </div>
                                  <AudioPlayer 
                                    audioUrl={phrase.audio_url} 
                                    size="sm" 
                                    onListened={() => markSituationPhraseListened(s.id, i, totalPhrases)}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    );
                  })
                )}
              </motion.div>
            )}

            {/* Culture Tab - A-lMaghrib Discovery */}
            {activeTab === 'culture' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-3 rounded-xl">
                    <Compass className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-2xl font-bold text-charcoal">
                      {language === 'en' ? 'A-lMaghrib Discovery' : 'Découverte A-lMaghrib'}
                    </h3>
                    <p className="font-nunito text-charcoal/60">
                      {language === 'en' 
                        ? 'Explore Moroccan culture and traditions' 
                        : 'Explorez la culture et les traditions marocaines'}
                    </p>
                  </div>
                </div>

                {culture.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🇲🇦</div>
                    <p className="text-charcoal/50 font-nunito">
                      {language === 'en' 
                        ? 'Cultural content coming soon!' 
                        : 'Contenu culturel bientôt disponible !'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {culture.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-stone-200 hover:shadow-xl transition-all"
                      >
                        {item.image_url && (
                          <div className="h-48 overflow-hidden">
                            <img 
                              src={`${BACKEND_URL}${item.image_url}`}
                              alt={item.title}
                              className="w-full h-full object-contain bg-stone-100"
                            />
                          </div>
                        )}
                        <div className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-4xl">{item.emoji}</span>
                            <h4 className="font-playfair text-xl font-bold text-charcoal">
                              {item.title}
                            </h4>
                          </div>
                          <p className="font-nunito text-charcoal/80 leading-relaxed">
                            {language === 'en' ? item.description_en : item.description_fr}
                          </p>
                          <div className="mt-4 pt-4 border-t border-stone-200">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-nunito font-semibold">
                              {item.category === 'traditions' && '🎭'}
                              {item.category === 'etiquette' && '🤝'}
                              {item.category === 'daily_life' && '☀️'}
                              {item.category === 'gastronomy' && '🍽️'}
                              {item.category === 'traditions' ? (language === 'en' ? 'Traditions' : 'Traditions') : 
                               item.category === 'etiquette' ? (language === 'en' ? 'Etiquette' : 'Étiquette') :
                               item.category === 'daily_life' ? (language === 'en' ? 'Daily Life' : 'Vie quotidienne') :
                               (language === 'en' ? 'Gastronomy' : 'Gastronomie')}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

          </>
        )}
      </div>
    </div>
  );
};

export default LevelPage;
